import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiFile, FiPaperclip, FiSend, FiX, FiEdit2, FiTrash2 } from 'react-icons/fi';
import {
  searchChatMessages,
  sendDirectMessage,
  uploadMessageFileToTempFile,
  getMessage,
  modifyDirectMessage,
  deleteMessage,
} from '../api/NbmessageApi';
import { getAccessToken, useUserInfoQuery, useInvalidateUnreadMessageCount, getNativeWebview } from '../react-queries/queries';
import { processApiResponse, ERROR_CODES } from '../common/errorMessage';
import LoadingOverlay from '../components/LoadingOverlay';
import MessageModal from '../components/MessageModal';
import { useNoobookContext } from '../context/NoobookContext';
import { launchUrl } from '../api/native/NativeApis';

const BOTTOM_TAB_HEIGHT = 64;
const TOP_NAV_HEIGHT = 56;

const Wrapper = styled.div`
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  height: calc(100dvh - ${TOP_NAV_HEIGHT}px - ${BOTTOM_TAB_HEIGHT}px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ChatBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 16px 8px;
  background: #f8f9fa;
`;

const DateDivider = styled.div`
  text-align: center;
  margin: 16px 0;
  font-size: 12px;
  color: #888;
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${props => props.$isMine ? 'flex-end' : 'flex-start'};
  margin-bottom: 12px;
`;

const BubbleColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$isMine ? 'flex-end' : 'flex-start'};
  max-width: 75%;
`;

const Bubble = styled.div`
  padding: 10px 14px;
  border-radius: ${props => props.$isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
  background: ${props => props.$isMine ? '#244AC2' : '#fff'};
  color: ${props => props.$isMine ? '#fff' : '#222'};
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  border: ${props => props.$isMine ? 'none' : '1px solid #eee'};
`;

const BubbleTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  opacity: 0.9;
`;

const BubbleTime = styled.div`
  font-size: 11px;
  color: #999;
  margin-top: 4px;
`;

const BubbleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
`;

const BubbleActionButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f0f0;
    color: #555;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditModeBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f0f4ff;
  border-radius: 8px;
  font-size: 13px;
  color: #244AC2;
`;

const EditModeCancelButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 12px;
  padding: 4px 8px;

  &:hover {
    color: #222;
  }
`;

const AttachmentList = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const AttachmentItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 4px;
  border: 1px solid ${props => props.$isMine ? 'rgba(255,255,255,0.3)' : '#eee'};
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  color: ${props => props.$isMine ? '#fff' : '#666'};
  background: ${props => props.$isMine ? 'rgba(255,255,255,0.15)' : '#fafafa'};
  flex-shrink: 0;
`;

const AttachmentThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AttachmentFileName = styled.span`
  font-size: 8px;
  margin-top: 2px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  padding: 0 2px;
`;

const InputArea = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
  background: #fff;
  z-index: 99;
`;

const TitleInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #244AC2;
  }

  &::placeholder {
    color: #999;
  }
`;

const InputMessageRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const MessageInput = styled.textarea`
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  border: 1px solid #ddd;
  border-radius: 20px;
  padding: 10px 16px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  outline: none;
  line-height: 1.4;

  &:focus {
    border-color: #244AC2;
  }

  &::placeholder {
    color: #999;
  }
`;

const AttachButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: #f0f0f0;
  color: #555;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: #244AC2;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PendingFileList = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
`;

const PendingFileItem = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
`;

const PendingFileThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PendingFileName = styled.span`
  font-size: 7px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  padding: 0 2px;
`;

const RemoveFileButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const EmptyChat = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 14px;
`;

const LoadMoreHint = styled.div`
  text-align: center;
  padding: 8px;
  color: #999;
  font-size: 12px;
`;

const PAGE_SIZE = 20;
const MAX_ATTACH_FILES = 10;
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const isImageFile = (file) => {
  const ext = (file.fileExt || '').toLowerCase().replace(/^\./, '');
  if (IMAGE_EXTENSIONS.includes(ext)) {
    return true;
  }
  const name = (file.fileName || '').toLowerCase();
  return IMAGE_EXTENSIONS.some(e => name.endsWith('.' + e));
};

const formatChatDate = (dateString) => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return '오늘';
  }
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  });
};

const formatChatTime = (dateString) => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  return date.toLocaleTimeString('ko-KR', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const getDateKey = (dateString) => {
  if (!dateString) {
    return '';
  }
  return new Date(dateString).toDateString();
};

const groupMessagesWithDates = (messages) => {
  const items = [];
  let lastDateKey = '';

  messages.forEach((message) => {
    const dateKey = getDateKey(message.createdAt);
    if (dateKey && dateKey !== lastDateKey) {
      items.push({ type: 'date', key: `date-${dateKey}`, label: formatChatDate(message.createdAt) });
      lastDateKey = dateKey;
    }
    items.push({ type: 'message', key: `msg-${message.id}`, message });
  });

  return items;
};

export default function MessageChatPage() {
  const navigate = useNavigate();
  const query = useQuery();
  const { showBack, setShowBack, logoText, setLogoText } = useNoobookContext();
  const { data: userInfo } = useUserInfoQuery();

  const othersId = Number(query.get('othersId'));
  const othersName = query.get('othersName') || '';
  const othersLoginid = query.get('othersLoginid') || '';
  const noReply = query.get('noReply') === 'true';
  const [messages, setMessages] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [msgText, setMsgText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  //const invalidateUnreadMessageCount = useInvalidateUnreadMessageCount();

  const chatBodyRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const shouldScrollToBottomRef = useRef(false);

  const displayName = othersName || othersLoginid || '채팅';

  useEffect(() => {
    if (!othersId) {
      navigate('/messages', { replace: true });
    }
  }, [othersId, navigate]);

  useEffect(() => {
    const prevShowBack = showBack;
    const prevLogoText = logoText;
    setShowBack(true);
    setLogoText(displayName);
    return () => {
      setShowBack(prevShowBack);
      setLogoText(prevLogoText);
    };
  }, [setShowBack, setLogoText, displayName]);

  const scrollToBottom = useCallback(() => {
    const el = chatBodyRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const fetchChatMessages = useCallback(async (isRefresh = false) => {
    if (!othersId) {
      return;
    }

    if (isRefresh) {
      setInitialLoading(true);
      setCurrentPage(0);
      setHasMore(true);
      shouldScrollToBottomRef.current = true;
    } else {
      setIsLoadingMore(true);
      const el = chatBodyRef.current;
      prevScrollHeightRef.current = el ? el.scrollHeight : 0;
    }
    setError('');

    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        setError('인증 토큰을 가져올 수 없습니다.');
        return;
      }

      const page = isRefresh ? 0 : currentPage;
      const response = await searchChatMessages(accessToken, othersId, '', page, PAGE_SIZE);
      const processedResponse = processApiResponse(response);

      if (processedResponse.success && processedResponse.data) {
        const newData = [...processedResponse.data].reverse();

        if (isRefresh) {
          setMessages(newData);
        } else {
          setMessages(prev => [...newData, ...prev]);
        }

        setHasMore(processedResponse.data.length === PAGE_SIZE);
        setCurrentPage(page + 1);
      } else {
        setError(processedResponse.errorMessage || '채팅 메시지를 가져오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('채팅 메시지 조회 오류:', err);
      setError(err.message || '채팅 메시지를 가져오는 중 오류가 발생했습니다.');
    } finally {
      setInitialLoading(false);
      setIsLoadingMore(false);
    }
  }, [othersId, currentPage]);

  useEffect(() => {
    if (othersId) {
      fetchChatMessages(true);
    }
  }, [othersId]);

  useEffect(() => {
    if (shouldScrollToBottomRef.current && !initialLoading) {
      scrollToBottom();
      shouldScrollToBottomRef.current = false;
    } else if (prevScrollHeightRef.current > 0 && chatBodyRef.current) {
      const el = chatBodyRef.current;
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [messages, initialLoading, scrollToBottom]);

  const handleScroll = useCallback(() => {
    const el = chatBodyRef.current;
    if (!el || !hasMore || initialLoading || isLoadingMore) {
      return;
    }
    if (el.scrollTop < 80) {
      fetchChatMessages(false);
    }
  }, [hasMore, initialLoading, isLoadingMore, fetchChatMessages]);

  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files);
    if ((selectedFiles.length + files.length) > MAX_ATTACH_FILES) {
      setErrorMessage(`첨부 파일은 최대 ${MAX_ATTACH_FILES}개까지 가능합니다.`);
      return;
    }

    files.forEach((file) => {
      const fileItem = {
        file,
        preview: null,
        id: Date.now() + Math.random(),
        fileName: file.name,
        fileExt: file.name.includes('.') ? file.name.split('.').pop() : '',
      };

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedFiles(prev => [...prev, { ...fileItem, preview: e.target.result }]);
        };
        reader.readAsDataURL(file);
      } else {
        setSelectedFiles(prev => [...prev, fileItem]);
      }
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFiles]);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const resetInputForm = () => {
    setTitle('');
    setMsgText('');
    setSelectedFiles([]);
    setEditingMessageId(null);
  };

  const buildFileDTOs = async (accessToken) => {
    const fileDTOs = [];
    for (const fileItem of selectedFiles) {
      if (fileItem.messageFileId) {
        fileDTOs.push({
          id: fileItem.messageFileId,
          fileStoreId: fileItem.fileStoreId,
          fileName: fileItem.fileName,
          fileExt: fileItem.fileExt,
          fileUrl: fileItem.fileUrl || null,
          fileNo: fileItem.fileNo,
        });
        continue;
      }

      const response = await uploadMessageFileToTempFile(accessToken, fileItem.file);
      const processedResponse = processApiResponse(response);
      if (processedResponse.success && processedResponse.data) {
        fileDTOs.push({
          id: null,
          fileStoreId: processedResponse.data.fileId,
          fileName: processedResponse.data.fileNm,
          fileExt: processedResponse.data.ext,
          fileUrl: processedResponse.data.fileUrl,
          fileNo: null,
        });
      } else {
        throw new Error(processedResponse.errorMessage || '파일 업로드에 실패했습니다.');
      }
    }
    return fileDTOs;
  };

  const handleEditMessage = (message) => {
    if (isSending) {
      return;
    }

    setEditingMessageId(message.id);
    setTitle(message.title || '');
    setMsgText(message.msgText || '');
    setSelectedFiles((message.files || []).map(file => ({
      file: null,
      preview: isImageFile(file) && file.fileUrl ? file.fileUrl : null,
      id: Date.now() + Math.random(),
      messageFileId: file.id,
      fileName: file.fileName,
      fileExt: file.fileExt,
      fileStoreId: file.fileStoreId,
      fileUrl: file.fileUrl,
      fileNo: file.fileNo,
    })));
    shouldScrollToBottomRef.current = true;
  };

  const handleCancelEdit = () => {
    resetInputForm();
  };

  const handleDeleteMessage = async (messageId) => {
    if (isSending) {
      return;
    }
    if (!window.confirm('정말로 이 메시지를 삭제하시겠습니까?')) {
      return;
    }

    setIsSending(true);
    setErrorMessage('');

    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        setErrorMessage('인증 토큰을 가져올 수 없습니다.');
        return;
      }

      const response = await deleteMessage(accessToken, messageId);
      const processedResponse = processApiResponse(response);

      if (processedResponse.success || response?.errorCode === ERROR_CODES.NOTFOUND) {
        setMessages(prev => prev.filter(item => item.id !== messageId));
        if (editingMessageId === messageId) {
          resetInputForm();
        }
      } else {
        setErrorMessage(processedResponse.errorMessage || '메시지 삭제에 실패했습니다.');
      }
    } catch (err) {
      console.error('메시지 삭제 오류:', err);
      setErrorMessage(err.message || '메시지 삭제 중 오류가 발생했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = async () => {
    const text = msgText.trim();
    if ((!text && selectedFiles.length === 0) || isSending || !othersId) {
      return;
    }

    setIsSending(true);
    setErrorMessage('');

    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        setErrorMessage('인증 토큰을 가져올 수 없습니다.');
        return;
      }

      const fileDTOs = await buildFileDTOs(accessToken);

      const reqDto = {
        toUserId: othersId,
        title: title.trim() || null,
        msgText: text || null,
        files: fileDTOs.length > 0 ? fileDTOs : null,
      };

      if (editingMessageId) {
        const response = await modifyDirectMessage(accessToken, editingMessageId, reqDto);
        const processedResponse = processApiResponse(response);

        if (processedResponse.success && processedResponse.data?.id) {
          const messageResponse = await getMessage(accessToken, processedResponse.data.id);
          const processedMessageResponse = processApiResponse(messageResponse);

          if (processedMessageResponse.success && processedMessageResponse.data) {
            setMessages(prev =>
              prev.map(item =>
                item.id === editingMessageId ? processedMessageResponse.data : item
              )
            );
            resetInputForm();
          } else {
            setErrorMessage(processedMessageResponse.errorMessage || '메시지 조회에 실패했습니다.');
          }
        } else {
          setErrorMessage(processedResponse.errorMessage || '메시지 수정에 실패했습니다.');
        }
        return;
      }

      const response = await sendDirectMessage(accessToken, reqDto);
      const processedResponse = processApiResponse(response);

      if (processedResponse.success && processedResponse.data?.id) {
        const messageResponse = await getMessage(accessToken, processedResponse.data.id);
        const processedMessageResponse = processApiResponse(messageResponse);

        if (processedMessageResponse.success && processedMessageResponse.data) {
          setMessages(prev => [...prev, processedMessageResponse.data]);
          resetInputForm();
          shouldScrollToBottomRef.current = true;
        } else {
          setErrorMessage(processedMessageResponse.errorMessage || '메시지 조회에 실패했습니다.');
        }
      } else {
        setErrorMessage(processedResponse.errorMessage || '메시지 전송에 실패했습니다.');
      }
    } catch (err) {
      console.error('메시지 처리 오류:', err);
      setErrorMessage(err.message || '메시지 처리 중 오류가 발생했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderAttachment = (file, index, isMine) => {
    const fileName = file.fileName || `첨부파일${index + 1}`;
    const downloadUrl = file.fileDownloadUrl;
    const downloadProps = {
      href: downloadUrl || '#',
      download: fileName,
      target: '_blank',
      rel: 'noopener noreferrer',
      onClick: (e) => {
        if (!downloadUrl) {
          e.preventDefault();
          return;
        }
        if (getNativeWebview()) {
          e.preventDefault();
          launchUrl(downloadUrl);
        }
      },
    };

    if (isImageFile(file) && file.fileUrl) {
      return (
        <AttachmentItem key={file.id || index} $isMine={isMine} {...downloadProps} title={fileName}>
          <AttachmentThumbnail src={file.fileUrl} alt={fileName} />
        </AttachmentItem>
      );
    }

    return (
      <AttachmentItem key={file.id || index} $isMine={isMine} {...downloadProps} title={fileName}>
        <FiFile size={20} />
        <AttachmentFileName>{fileName}</AttachmentFileName>
      </AttachmentItem>
    );
  };

  const renderMessageBubble = (message) => {
    const isMine = message.frUser?.id != null && message.frUser.id === userInfo?.id;
    const hasFiles = message.files && message.files.length > 0;

    return (
      <MessageRow key={message.id} $isMine={isMine}>
        <BubbleColumn $isMine={isMine}>
          <Bubble $isMine={isMine}>
            {message.title && <BubbleTitle>{message.title}</BubbleTitle>}
            {message.msgText}
            {hasFiles && (
              <AttachmentList>
                {message.files.map((file, index) => renderAttachment(file, index, isMine))}
              </AttachmentList>
            )}
          </Bubble>
          {isMine && (
            <BubbleActions>
              <BubbleActionButton
                type="button"
                onClick={() => handleEditMessage(message)}
                disabled={isSending}
                aria-label="수정"
                title="수정"
              >
                <FiEdit2 size={12} />
              </BubbleActionButton>
              <BubbleActionButton
                type="button"
                onClick={() => handleDeleteMessage(message.id)}
                disabled={isSending}
                aria-label="삭제"
                title="삭제"
              >
                <FiTrash2 size={12} />
              </BubbleActionButton>
            </BubbleActions>
          )}
          <BubbleTime>{formatChatTime(message.createdAt)}</BubbleTime>
        </BubbleColumn>
      </MessageRow>
    );
  };

  if (!othersId) {
    return null;
  }

  if (initialLoading && messages.length === 0) {
    return <LoadingOverlay />;
  }

  const chatItems = groupMessagesWithDates(messages);

  return (
    <Wrapper>
      <ChatBody ref={chatBodyRef} onScroll={handleScroll}>
        {isLoadingMore && (
          <LoadMoreHint>이전 메시지 불러오는 중...</LoadMoreHint>
        )}

        {error && messages.length === 0 ? (
          <EmptyChat>
            <p>{error}</p>
            <button
              onClick={() => fetchChatMessages(true)}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: '#244AC2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              다시 시도
            </button>
          </EmptyChat>
        ) : messages.length === 0 ? (
          <EmptyChat>대화 내역이 없습니다.<br />메시지를 내보세요.</EmptyChat>
        ) : (
          chatItems.map((item) => {
            if (item.type === 'date') {
              return <DateDivider key={item.key}>{item.label}</DateDivider>;
            }
            return renderMessageBubble(item.message);
          })
        )}
      </ChatBody>

      {!noReply && (
      <InputArea>
        {editingMessageId && (
          <EditModeBar>
            <span>메시지 수정 중</span>
            <EditModeCancelButton type="button" onClick={handleCancelEdit}>
              취소
            </EditModeCancelButton>
          </EditModeBar>
        )}
        <TitleInput
          type="text"
          placeholder="제목 (선택)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        {selectedFiles.length > 0 && (
          <PendingFileList>
            {selectedFiles.map((fileItem) => (
              <PendingFileItem key={fileItem.id} title={fileItem.fileName}>
                {fileItem.preview ? (
                  <PendingFileThumbnail src={fileItem.preview} alt={fileItem.fileName} />
                ) : (
                  <>
                    <FiFile size={18} />
                    <PendingFileName>{fileItem.fileName}</PendingFileName>
                  </>
                )}
                <RemoveFileButton
                  type="button"
                  onClick={() => handleRemoveFile(fileItem.id)}
                  aria-label="첨부 파일 삭제"
                >
                  <FiX size={10} />
                </RemoveFileButton>
              </PendingFileItem>
            ))}
          </PendingFileList>
        )}
        <InputMessageRow>
          <AttachButton
            type="button"
            onClick={handleAttachClick}
            disabled={isSending || selectedFiles.length >= MAX_ATTACH_FILES}
            aria-label="파일 첨부"
          >
            <FiPaperclip size={18} />
          </AttachButton>
          <MessageInput
            placeholder="메시지를 입력하세요"
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            onKeyDown={handleInputKeyDown}
            rows={1}
          />
          <SendButton
            onClick={handleSend}
            disabled={isSending || (!msgText.trim() && selectedFiles.length === 0)}
            aria-label={editingMessageId ? '수정' : '보내기'}
          >
            <FiSend size={18} />
          </SendButton>
        </InputMessageRow>
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
        />
      </InputArea>
      )}

      <MessageModal
        isOpen={!!errorMessage}
        message={errorMessage}
        onClose={() => setErrorMessage('')}
        title="알림"
      />

      {isSending && <LoadingOverlay />}
    </Wrapper>
  );
}
